/**
 * Auth0 session initialization for internal (@tempus.com) users.
 *
 * Internal users authenticate directly with Okta (bypassing the Auth0 IdP daisy-chain). Before
 * navigating to a downstream Auth0-only app, account-ui redirects the user through Auth0's
 * /authorize endpoint with `connection=<okta-enterprise>` so Auth0 federates back to Okta (which
 * already has an active session), establishing an Auth0 SSO session cookie. The Auth0 authorization
 * code returned to the callback is intentionally discarded — the only purpose is the session cookie.
 */
import envConfig from '../config/config';
import { getOauthBrowsingContextOrigin } from './okta-config';

const INTERNAL_EMAIL_DOMAIN = '@tempus.com';

export const AUTH0_SESSION_CALLBACK_PATH = '/callback/auth0';

const AUTH0_SESSION_INIT_STATE_STORAGE_NAME = 'auth0_session_init_state';

// Cross-tab coordination (localStorage is shared by every tab; sessionStorage is not).
const AUTH0_SESSION_INIT_LEASE_STORAGE_NAME = 'auth0_session_init_lease';
const AUTH0_SESSION_INIT_DONE_STORAGE_NAME = 'auth0_session_init_done';
const AUTH0_SESSION_INIT_LOCK_NAME = 'auth0-session-init';

// A hop normally completes in ~1s; past this, assume it was abandoned (Auth0 error page, tab closed).
export const AUTH0_SESSION_INIT_LEASE_TTL_MS = 30000;
// Lets a tab that arrives right after another tab's hop (e.g. the router handing off to SignIn) skip
// its own. Tabs that waited on a specific lease match it by state instead, so throttled background
// tabs aren't bound by this window. This is not a session cache.
export const AUTH0_SESSION_INIT_DONE_TTL_MS = 10000;
// Keeps the Web Lock held after claiming so the lease write reaches other tabs before they read it.
const LOCK_HOLD_MS = 1000;
// Without Web Locks: re-read after this long to detect a tab that claimed at the same instant.
const FALLBACK_CLAIM_VERIFY_MS = 150;
const FALLBACK_CLAIM_JITTER_MS = 100;
const LEASE_POLL_INTERVAL_MS = 250;
const MAX_LEASE_WAITS = 5;

export function isInternalUser(email) {
  return typeof email === 'string' && email.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN);
}

export function isAuth0SessionInitConfigured() {
  const { sessionInitDomain, sessionInitClientId, sessionInitConnection } = envConfig.auth0 || {};
  return !!(sessionInitDomain && sessionInitClientId && sessionInitConnection);
}

export function needsAuth0SessionInit(email) {
  return isInternalUser(email) && isAuth0SessionInitConfigured();
}

export function buildAuth0SessionInitAuthorizeUrl(email, state) {
  const { sessionInitDomain, sessionInitClientId, sessionInitConnection } = envConfig.auth0 || {};
  const redirectUri = `${getOauthBrowsingContextOrigin()}${AUTH0_SESSION_CALLBACK_PATH}`;

  const params = new URLSearchParams({
    client_id: sessionInitClientId,
    connection: sessionInitConnection,
    login_hint: email,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid',
    state,
  });

  return `https://${sessionInitDomain}/authorize?${params.toString()}`;
}

/** The browser is navigating to Auth0; callers must not start a competing navigation. */
export const AUTH0_SESSION_INIT_REDIRECTING = 'redirecting';
/** Another tab just established the Auth0 session for this user; continue without a hop. */
export const AUTH0_SESSION_INIT_COMPLETED_ELSEWHERE = 'completed-elsewhere';

/**
 * Starts the Auth0 session-init hop, unless another tab of this browser is already running it.
 *
 * okta-auth-js syncs freshly stored tokens to every open account-ui tab, so all of them decide to hop
 * at the same moment. With no Auth0 session yet, each concurrent /authorize mints its own `auth0`
 * cookie; the last write wins and every other tab lands on Auth0's "couldn't find your session" page.
 * Only one tab may hop at a time: the others wait for it to finish and then continue without hopping.
 *
 * @param {string} email
 * @returns {Promise<'redirecting' | 'completed-elsewhere'>}
 */
export async function startAuth0SessionInit(email) {
  let waitedLeaseState = null;
  for (let attempt = 0; attempt <= MAX_LEASE_WAITS; attempt += 1) {
    const outcome = await claimAuth0SessionInit(email, waitedLeaseState);
    if (outcome.type === 'claimed') {
      redirectToAuth0SessionInit(email, outcome.state);
      return AUTH0_SESSION_INIT_REDIRECTING;
    }
    if (outcome.type === 'done') {
      return AUTH0_SESSION_INIT_COMPLETED_ELSEWHERE;
    }
    if (attempt === MAX_LEASE_WAITS) {
      break;
    }
    waitedLeaseState = outcome.lease.state;
    await waitForLeaseToSettle(outcome.lease);
  }
  // Coordination keeps failing: hop uncoordinated rather than leave the user without an Auth0 session.
  redirectToAuth0SessionInit(email, generateState());
  return AUTH0_SESSION_INIT_REDIRECTING;
}

/**
 * Called from the Auth0 callback: frees the lease for waiting tabs and, when Auth0 returned an
 * authorization code (i.e. established its session), tells them so they don't hop again.
 *
 * @param {string | null} returnedState
 * @param {{ completed: boolean }} options
 */
export function releaseAuth0SessionInitLease(returnedState, { completed }) {
  try {
    const lease = readJson(AUTH0_SESSION_INIT_LEASE_STORAGE_NAME);
    if (!returnedState || lease?.state !== returnedState) {
      return;
    }
    if (completed) {
      localStorage.setItem(
        AUTH0_SESSION_INIT_DONE_STORAGE_NAME,
        JSON.stringify({ state: lease.state, email: normalizeEmail(lease.email), at: Date.now() }),
      );
    }
    localStorage.removeItem(AUTH0_SESSION_INIT_LEASE_STORAGE_NAME);
  } catch {
    // Storage unavailable: waiting tabs fall back to the lease TTL.
  }
}

export function verifyAndClearAuth0SessionInitState(returnedState) {
  const expected = sessionStorage.getItem(AUTH0_SESSION_INIT_STATE_STORAGE_NAME);
  sessionStorage.removeItem(AUTH0_SESSION_INIT_STATE_STORAGE_NAME);
  return !!(expected && expected === returnedState);
}

function generateState() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

function redirectToAuth0SessionInit(email, state) {
  sessionStorage.setItem(AUTH0_SESSION_INIT_STATE_STORAGE_NAME, state);
  window.location.assign(buildAuth0SessionInitAuthorizeUrl(email, state));
}

/**
 * Atomically decides whether this tab hops (`claimed`), waits for another tab (`wait`), or can skip
 * the hop because another tab just finished it for the same user (`done`).
 */
async function claimAuth0SessionInit(email, waitedLeaseState) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks?.request) {
    const outcome = await claimWithWebLock(locks, email, waitedLeaseState);
    if (outcome) {
      return outcome;
    }
  }
  return claimWithoutWebLock(email, waitedLeaseState);
}

function claimWithWebLock(locks, email, waitedLeaseState) {
  return new Promise((resolve) => {
    let settled = false;
    locks
      .request(AUTH0_SESSION_INIT_LOCK_NAME, async () => {
        const outcome = inspectOrClaimLease(email, waitedLeaseState);
        settled = true;
        resolve(outcome);
        if (outcome.type === 'claimed') {
          // Held until this document unloads for the Auth0 redirect (or the hold elapses).
          await sleep(LOCK_HOLD_MS);
        }
      })
      .catch(() => {
        if (!settled) {
          resolve(null);
        }
      });
  });
}

async function claimWithoutWebLock(email, waitedLeaseState) {
  await sleep(Math.random() * FALLBACK_CLAIM_JITTER_MS);
  const outcome = inspectOrClaimLease(email, waitedLeaseState);
  if (outcome.type !== 'claimed' || outcome.unsynchronized) {
    return outcome;
  }
  await sleep(FALLBACK_CLAIM_VERIFY_MS);
  const lease = readJson(AUTH0_SESSION_INIT_LEASE_STORAGE_NAME);
  if (lease && lease.state !== outcome.state) {
    return { type: 'wait', lease };
  }
  return outcome;
}

function inspectOrClaimLease(email, waitedLeaseState) {
  try {
    const now = Date.now();
    const done = readJson(AUTH0_SESSION_INIT_DONE_STORAGE_NAME);
    const completedForThisUser =
      done &&
      done.email === normalizeEmail(email) &&
      ((waitedLeaseState && done.state === waitedLeaseState) || now - done.at < AUTH0_SESSION_INIT_DONE_TTL_MS);
    if (completedForThisUser) {
      return { type: 'done' };
    }
    const lease = readJson(AUTH0_SESSION_INIT_LEASE_STORAGE_NAME);
    if (isLeaseActive(lease, now)) {
      return { type: 'wait', lease };
    }
    const state = generateState();
    localStorage.setItem(
      AUTH0_SESSION_INIT_LEASE_STORAGE_NAME,
      JSON.stringify({ state, email: normalizeEmail(email), expiresAt: now + AUTH0_SESSION_INIT_LEASE_TTL_MS }),
    );
    return { type: 'claimed', state };
  } catch {
    // localStorage unavailable (e.g. blocked by privacy settings): hop without coordination.
    return { type: 'claimed', state: generateState(), unsynchronized: true };
  }
}

/**
 * Resolves once the given lease is released, replaced, or expired. Listens for cross-tab `storage`
 * events so the wait ends promptly, and polls as a fallback (background-tab timers are throttled).
 */
function waitForLeaseToSettle(lease) {
  return new Promise((resolve) => {
    let timer;
    const finish = () => {
      clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
      resolve();
    };
    const check = () => {
      let current;
      try {
        current = readJson(AUTH0_SESSION_INIT_LEASE_STORAGE_NAME);
      } catch {
        current = null;
      }
      if (current?.state === lease.state && isLeaseActive(current, Date.now())) {
        clearTimeout(timer);
        timer = setTimeout(check, LEASE_POLL_INTERVAL_MS);
        return;
      }
      finish();
    };
    function onStorage(event) {
      if (
        event.key === null ||
        event.key === AUTH0_SESSION_INIT_LEASE_STORAGE_NAME ||
        event.key === AUTH0_SESSION_INIT_DONE_STORAGE_NAME
      ) {
        check();
      }
    }
    window.addEventListener('storage', onStorage);
    timer = setTimeout(check, LEASE_POLL_INTERVAL_MS);
  });
}

function isLeaseActive(lease, now) {
  return !!(lease && typeof lease.expiresAt === 'number' && lease.expiresAt > now);
}

function readJson(key) {
  const raw = localStorage.getItem(key);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizeEmail(email) {
  return typeof email === 'string' ? email.toLowerCase() : null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
